import React, { useState, useMemo, useEffect, useRef, lazy, Suspense } from 'react';
import Header from './components/Header';
import LoginPage from './components/LoginPage';
const AccountPage = lazy(() => import('./components/AccountPage'));
const InviteAdminPage = lazy(() => import('./components/InviteAdminPage'));
import InviteRegisterPage from './components/InviteRegisterPage';
import WaitlistPage from './components/WaitlistPage';
const WaitlistAdminPage = lazy(() => import('./components/WaitlistAdminPage'));
const SiteModeAdminPage = lazy(() => import('./components/SiteModeAdminPage'));
const FeedbackAdminPage = lazy(() => import('./components/FeedbackAdminPage'));
const MessagesAdminPage = lazy(() => import('./components/MessagesAdminPage'));
const UserDirectoryPage = lazy(() => import('./components/UserDirectoryPage'));
const BillingAdminPage = lazy(() => import('./components/BillingAdminPage'));
const CharityAdminPage = lazy(() => import('./components/CharityAdminPage'));
const BannerAdminPage = lazy(() => import('./components/BannerAdminPage'));
const NavVisibilityAdminPage = lazy(() => import('./components/NavVisibilityAdminPage'));
const MacroCalendarAdminPage = lazy(() => import('./components/MacroCalendarAdminPage'));
const SecResearchPage = lazy(() => import('./components/SecResearchPage'));
const AdminAnalyticsPage = lazy(() => import('./components/AdminAnalyticsPage'));
const BackupMigrationPage = lazy(() => import('./components/BackupMigrationPage'));
import FeedbackWidget from './components/FeedbackWidget';
import TesterChatWidget from './components/TesterChatWidget';
import BirthdayCelebrationPage from './components/BirthdayCelebrationPage';
import BannerBar from './components/BannerBar';
import { usePresenceHeartbeat } from './hooks/usePresenceHeartbeat';
import MaintenancePage from './components/MaintenancePage';
import LaunchCelebrationPage from './components/LaunchCelebrationPage';
import SalesPage from './components/SalesPage';
import Navigation from './components/Navigation';
import MasterSignalLight from './components/MasterSignalLight';
import SignalSummary from './components/SignalSummary';
import ActionPanel from './components/ActionPanel';
import SubSignalGrid from './components/SubSignalGrid';
import KeyMetrics from './components/KeyMetrics';
import LiveModeIndicator from './components/LiveModeIndicator';
import SignalHistory from './components/SignalHistory';
import RiskRegimeBoundaries from './components/RiskRegimeBoundaries';
import DataFreshnessConfidence from './components/DataFreshnessConfidence';
const YieldCurvePage = lazy(() => import('./components/YieldCurvePage'));
const VigilantePage = lazy(() => import('./components/VigilantePage'));
const FearGaugePage = lazy(() => import('./components/FearGaugePage'));
const MoneyFlowsPage = lazy(() => import('./components/MoneyFlowsPage'));
const StrategistPage = lazy(() => import('./components/StrategistPage'));
const LearnPage = lazy(() => import('./components/LearnPage'));
const SimulatorPage = lazy(() => import('./components/SimulatorPage'));
const AdminHealthPage = lazy(() => import('./components/AdminHealthPage'));
const HistoricalReplayPage = lazy(() => import('./components/HistoricalReplayPage'));
const RegimeAlertsPage = lazy(() => import('./components/RegimeAlertsPage'));
import WhatChangedSummary from './components/WhatChangedSummary';
import SignalStabilityNotice from './components/SignalStabilityNotice';
import MacroEventAwareness from './components/MacroEventAwareness';
import InstitutionalRiskProfiles from './components/InstitutionalRiskProfiles';
const BacktestingPage = lazy(() => import('./components/BacktestingPage'));
const ProbabilityPage = lazy(() => import('./components/ProbabilityPage'));
import PredictiveRegimeProbability from './components/PredictiveRegimeProbability';
import CollapsibleSection from './components/CollapsibleSection';
import AuctionWatchCard from './components/AuctionWatchCard';
import CommoditiesCard from './components/CommoditiesCard';
import Footer from './components/Footer';
import { computeMasterSignal, generateSignalSummary, ACTION_LEVELS } from './utils/signalEngine';
import { computeInstitutionalRiskProfiles } from './utils/institutionalRiskProfiles';
import { computeSignalCoverage } from './utils/signalCoverage';
import { SCENARIOS, SIGNAL_HISTORY, SUB_SIGNAL_META } from './data/marketData';
import { YIELD_CURVE_SCENARIOS, FED_WATCH_SCENARIOS } from './data/yieldCurveData';
import { VIGILANTE_SCENARIOS } from './data/vigilanteData';
import { FEAR_GAUGE_SCENARIOS } from './data/fearGaugeData';
import { MONEY_FLOW_SCENARIOS } from './data/moneyFlowsData';
import { STRATEGIST_SCENARIOS } from './data/strategistData';
import { formatDateTime } from './utils/dateUtils';
import useLiveData from './hooks/useLiveData';
import { fetchAuthStatus, fetchSiteMode, login as loginRequest, logout as logoutRequest, trackAnalyticsEvent, fetchBirthdayStatus, fetchNavVisibility, fetchAuctionWatch } from './services/apiClient';

const CURRENT_DROP = 7;
const APP_PATCH_VERSION = 'v43h-backup-restore-migration-center';
const SIGNAL_HISTORY_STORAGE_KEY = 'bondMentorSignalHistory_v34';

function parseHistoryDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function buildMinuteHistoryKey(entry) {
  const minuteBucket = parseHistoryDate(entry.date);
  minuteBucket.setSeconds(0, 0);
  return minuteBucket.toISOString();
}

function buildStateKey(entry) {
  return [
    String(entry.signal || '').toUpperCase(),
    String(Math.round(Number(entry.score) || 0)),
    String(entry.action || '').trim().toUpperCase(),
  ].join('|');
}

function buildStableRunId(liveData, live) {
  const sourceTime = live?.computedAt || liveData?.fetchedAt || new Date().toISOString();
  const minuteBucket = parseHistoryDate(sourceTime);
  minuteBucket.setSeconds(0, 0);
  return minuteBucket.toISOString();
}

function cleanSignalHistory(history) {
  if (!Array.isArray(history)) return [];

  const byMinute = new Map();

  history
    .filter((entry) => entry && entry.date && entry.signal && Number.isFinite(Number(entry.score)))
    .forEach((entry) => {
      const parsedDate = parseHistoryDate(entry.date);
      const parsedSavedAt = entry.savedAt ? parseHistoryDate(entry.savedAt) : parsedDate;
      const normalized = {
        ...entry,
        id: entry.id || `${buildMinuteHistoryKey(entry)}-${buildStateKey(entry)}`,
        date: parsedDate.toISOString(),
        savedAt: parsedSavedAt.toISOString(),
        score: Math.round(Number(entry.score)),
      };
      const key = buildMinuteHistoryKey(normalized);
      const existing = byMinute.get(key);
      const entryTime = parsedSavedAt.getTime();
      const existingTime = existing ? parseHistoryDate(existing.savedAt || existing.date).getTime() : -Infinity;

      // Keep exactly one row per displayed minute. If two states land in the
      // same minute, keep the newest state so the dashboard never shows
      // contradictory 4:44 PM rows.
      if (!existing || entryTime >= existingTime) {
        byMinute.set(key, normalized);
      }
    });

  const sorted = Array.from(byMinute.values())
    .sort((a, b) => parseHistoryDate(b.date).getTime() - parseHistoryDate(a.date).getTime());

  // Compress repeated polling rows. The dashboard should be a signal-change
  // history, not a minute-by-minute polling log. Keep a row only when the
  // score/regime/action state changes from the previous kept row.
  const compressed = [];
  for (const entry of sorted) {
    const previous = compressed[compressed.length - 1];
    if (previous && buildStateKey(previous) === buildStateKey(entry)) {
      continue;
    }
    compressed.push(entry);
  }

  return compressed.slice(0, 25);
}

function roundNumber(value, digits = 2) {
  const num = Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(digits)) : null;
}

function readFirstNumber(...values) {
  for (const value of values) {
    // Important: Number(null) and Number('') both become 0 in JavaScript.
    // That caused missing API fields to display as 0.00 instead of falling back
    // to the next real value or showing N/A.
    if (value === null || value === undefined || value === '' || value === '.') continue;
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

function buildLiveKeyMetrics(liveData) {
  if (!liveData) return null;

  const freshness = Array.isArray(liveData?.dataFreshness) ? liveData.dataFreshness : [];
  const freshnessByKey = freshness.reduce((acc, item) => {
    if (item?.key) acc[item.key] = item;
    return acc;
  }, {});

  function fallbackStatusForMetric(metricKey) {
    const map = {
      tenYearYield: 'yieldCurve',
      twoYearYield: 'yieldCurve',
      spreadTwoTen: 'yieldCurve',
      igSpread: 'creditSpreads',
      hySpread: 'creditSpreads',
      fedFundsRate: 'fed',
      moveIndex: 'move',
      vix: 'vix',
    };
    const feed = freshnessByKey[map[metricKey]];
    const status = feed?.status || '';
    const source = feed?.source || '';
    const text = `${status} ${source}`.toLowerCase();

    if (text.includes('verified backup') || text.includes('last clean')) return 'Verified backup snapshot';
    if (text.includes('safety baseline') || text.includes('configured')) return 'Safety baseline fallback';
    if (text.includes('estimated')) return 'Estimated value';
    if (text.includes('live') || text.includes('intraday') || text.includes('latest')) return 'Live / latest source';
    if (text.includes('optional')) return 'Optional feed';
    return '';
  }

  function withFallbackValue(metricKey, value, fallbackValue) {
    const numeric = Number(value);
    const zeroCanBeValid = metricKey === 'spreadTwoTen';
    const valid = value !== null && value !== undefined && value !== ''
      && !(Number.isFinite(numeric) && numeric === 0 && !zeroCanBeValid);
    if (valid) return value;

    const status = fallbackStatusForMetric(metricKey);
    if (status && fallbackValue !== null && fallbackValue !== undefined && fallbackValue !== '') return fallbackValue;
    return value;
  }

  const tenYear = readFirstNumber(
    liveData?.yieldCurve?.yields?.['10y'],
    liveData?.yieldCurve?.yields?.DGS10,
    liveData?.yieldCurve?.tenYearYield,
    liveData?.fed?.tenYearYield
  );
  const twoYear = readFirstNumber(
    liveData?.yieldCurve?.yields?.['2y'],
    liveData?.yieldCurve?.yields?.DGS2,
    liveData?.yieldCurve?.twoYearYield,
    liveData?.fed?.twoYearYield
  );
  const spreadTwoTen = Number.isFinite(tenYear) && Number.isFinite(twoYear)
    ? roundNumber(tenYear - twoYear, 2)
    : readFirstNumber(liveData?.yieldCurve?.spreads?.twoTen, liveData?.fed?.spread2Y10Y);

  const vixValue = readFirstNumber(
    liveData?.volatility?.vix?.current,
    liveData?.vix?.current,
    liveData?.vix?.value,
    liveData?.vix?.close,
    liveData?.vix?.latest
  );

  const fedFunds = liveData?.fed?.fedFundsRange
    || (Number.isFinite(Number(liveData?.fed?.fedFundsRate)) ? roundNumber(liveData.fed.fedFundsRate, 2) : null);

  const metricStatuses = {
    tenYearYield: fallbackStatusForMetric('tenYearYield'),
    twoYearYield: fallbackStatusForMetric('twoYearYield'),
    spreadTwoTen: fallbackStatusForMetric('spreadTwoTen'),
    fedFundsRate: fallbackStatusForMetric('fedFundsRate'),
    igSpread: fallbackStatusForMetric('igSpread'),
    hySpread: fallbackStatusForMetric('hySpread'),
    moveIndex: fallbackStatusForMetric('moveIndex'),
    vix: fallbackStatusForMetric('vix'),
  };

  return {
    tenYearYield: roundNumber(withFallbackValue('tenYearYield', tenYear, 4.48), 2),
    twoYearYield: roundNumber(withFallbackValue('twoYearYield', twoYear, 4.13), 2),
    spreadTwoTen: roundNumber(withFallbackValue('spreadTwoTen', spreadTwoTen, 0.35), 2),
    fedFundsRate: withFallbackValue('fedFundsRate', fedFunds, '3.50% - 3.75%'),
    igSpread: roundNumber(withFallbackValue('igSpread', readFirstNumber(liveData?.creditSpreads?.ig?.current, liveData?.creditSpreads?.investmentGrade?.current), 75), 0),
    hySpread: roundNumber(withFallbackValue('hySpread', readFirstNumber(liveData?.creditSpreads?.hy?.current, liveData?.creditSpreads?.highYield?.current), 280), 0),
    moveIndex: roundNumber(withFallbackValue('moveIndex', liveData?.volatility?.move?.current, 99), 0),
    vix: roundNumber(withFallbackValue('vix', vixValue, 17.5), 2),
    _statuses: metricStatuses,
  };
}

function determineCurveShapeFromSpreads(spreads) {
  const twoTen = Number(spreads?.twoTen);
  const threeMonthTen = Number(spreads?.threeMonthTen);

  if (Number.isFinite(twoTen) && twoTen < -0.20) return 'INVERTED';
  if (Number.isFinite(twoTen) && twoTen < -0.05) return 'INVERTING';
  if (Number.isFinite(threeMonthTen) && threeMonthTen < -0.10) return 'INVERTING';
  if (Number.isFinite(twoTen) && twoTen < 0.10) return 'FLAT';
  if (Number.isFinite(twoTen) && twoTen < 0.20) return 'FLATTENING';
  return 'NORMAL';
}

function formatBps(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const bps = Math.round(num * 100);
  return `${bps > 0 ? '+' : ''}${bps} bps`;
}

function formatPercent(value) {
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(2)}%` : '—';
}

function buildLiveYieldCurveData(liveData, fallbackCurveData) {
  const liveCurve = liveData?.yieldCurve;
  if (!liveCurve?.yields) return fallbackCurveData;

  const fallbackYields = fallbackCurveData?.yields || {};
  const yields = {
    '1m': roundNumber(readFirstNumber(liveCurve.yields['1m'], fallbackYields['1m']), 2),
    '3m': roundNumber(readFirstNumber(liveCurve.yields['3m'], fallbackYields['3m']), 2),
    '6m': roundNumber(readFirstNumber(liveCurve.yields['6m'], fallbackYields['6m']), 2),
    '1y': roundNumber(readFirstNumber(liveCurve.yields['1y'], fallbackYields['1y']), 2),
    '2y': roundNumber(readFirstNumber(liveCurve.yields['2y'], fallbackYields['2y']), 2),
    '3y': roundNumber(readFirstNumber(liveCurve.yields['3y'], fallbackYields['3y']), 2),
    '5y': roundNumber(readFirstNumber(liveCurve.yields['5y'], fallbackYields['5y']), 2),
    '7y': roundNumber(readFirstNumber(liveCurve.yields['7y'], fallbackYields['7y']), 2),
    '10y': roundNumber(readFirstNumber(liveCurve.yields['10y'], fallbackYields['10y']), 2),
    '20y': roundNumber(readFirstNumber(liveCurve.yields['20y'], fallbackYields['20y']), 2),
    '30y': roundNumber(readFirstNumber(liveCurve.yields['30y'], fallbackYields['30y']), 2),
  };

  const spreads = {
    twoTen: Number.isFinite(yields['10y']) && Number.isFinite(yields['2y'])
      ? roundNumber(yields['10y'] - yields['2y'], 2)
      : roundNumber(readFirstNumber(liveCurve.spreads?.twoTen, fallbackCurveData?.spreads?.twoTen), 2),
    threeMonthTen: Number.isFinite(yields['10y']) && Number.isFinite(yields['3m'])
      ? roundNumber(yields['10y'] - yields['3m'], 2)
      : roundNumber(readFirstNumber(liveCurve.spreads?.threeMonthTen, fallbackCurveData?.spreads?.threeMonthTen), 2),
    twoThirty: Number.isFinite(yields['30y']) && Number.isFinite(yields['2y'])
      ? roundNumber(yields['30y'] - yields['2y'], 2)
      : roundNumber(readFirstNumber(liveCurve.spreads?.twoThirty, fallbackCurveData?.spreads?.twoThirty), 2),
    fiveThirty: Number.isFinite(yields['30y']) && Number.isFinite(yields['5y'])
      ? roundNumber(yields['30y'] - yields['5y'], 2)
      : roundNumber(readFirstNumber(liveCurve.spreads?.fiveThirty, fallbackCurveData?.spreads?.fiveThirty), 2),
  };

  const curveShape = liveCurve.curveShape || determineCurveShapeFromSpreads(spreads);
  const treasurySource = liveCurve.intradaySource || liveCurve.yieldSources?.['10y'] || 'Shared live snapshot';
  const lastUpdated = liveCurve.fetchedAt || liveData?.fetchedAt || new Date().toISOString();

  return {
    ...fallbackCurveData,
    curveShape,
    yields,
    spreads,
    sourceLabel: 'Shared Live Snapshot',
    sourceDetail: treasurySource,
    lastUpdated,
    weeklyChange: liveCurve.weeklyChange || {
      twoTen: null,
      currentSpread: spreads.twoTen,
      isBaseline: true,
      label: 'Current 2s/10s baseline',
      direction: `Need one stored daily snapshot from about 7 days ago. Current live 2s/10s spread is ${formatBps(spreads.twoTen)}.`,
    },
    monthlyChange: liveCurve.monthlyChange || {
      twoTen: null,
      currentSpread: spreads.twoTen,
      isBaseline: true,
      label: 'Current 2s/10s baseline',
      direction: `Need one stored daily snapshot from about 30 days ago. Current live 2s/10s spread is ${formatBps(spreads.twoTen)}.`,
    },
    yieldSources: liveCurve.yieldSources || {},
    snapshotStore: liveCurve.snapshotStore || null,
    curveAnalysis:
      `The live yield curve is using the same shared market snapshot as Mission Control. ` +
      `The 10-year Treasury is ${formatPercent(yields['10y'])}, the 2-year Treasury is ${formatPercent(yields['2y'])}, ` +
      `and the 2Y/10Y spread is ${formatBps(spreads.twoTen)}. ` +
      `The 3M/10Y spread is ${formatBps(spreads.threeMonthTen)}. ` +
      `This prevents the Yield Curve page from displaying stale demo values that disagree with the dashboard.`
  };
}

function buildLiveFedWatchData(liveData, fallbackFedData, liveCurveData) {
  const fed = liveData?.fed;
  if (!fed) return fallbackFedData;

  const fedFundsRange = fed.fedFundsRange
    || (Number.isFinite(Number(fed.fedFundsLower)) && Number.isFinite(Number(fed.fedFundsUpper))
      ? `${Number(fed.fedFundsLower).toFixed(2)}% - ${Number(fed.fedFundsUpper).toFixed(2)}%`
      : fallbackFedData?.fedFundsRange);

  const effectiveFedFunds = readFirstNumber(fed.effectiveFedFundsRate, fed.fedFundsRate, fallbackFedData?.fedFundsRate);
  const tenYear = liveCurveData?.yields?.['10y'];
  const twoTen = liveCurveData?.spreads?.twoTen;

  return {
    ...fallbackFedData,
    fedFundsRate: roundNumber(effectiveFedFunds, 3),
    fedFundsRange,
    fedPosture: 'NEUTRAL',
    postureLabel: 'On Hold',
    postureEmoji: '⏸️',
    marketExpects: 'Holding steady while the bond market reprices inflation, growth, and supply concerns.',
    fedSays: `The policy target range is ${fedFundsRange || 'available from the live Fed feed'}. The Fed is not shown as actively easing or tightening in this dashboard state.`,
    bondsSay: `The live curve shows a ${formatPercent(tenYear)} 10-year Treasury and a ${formatBps(twoTen)} 2Y/10Y spread. This is pulled from the same shared snapshot used by Mission Control.`,
    disagreementLevel: liveData?.signal?.compositeScore || fallbackFedData?.disagreementLevel || 25,
    disagreementNote: 'The Fed and bond market reading is now tied to the shared live snapshot, so stale Yield Curve page values should no longer contradict the main dashboard.',
  };
}


/* ============================================================
   DROP B — "Detail Tabs Go Live"
   Build live versions of the Vigilantes, Fear Gauge, Money Flows,
   and Strategist tab data from the shared snapshot, so they stop
   showing demo numbers that contradict Mission Control. Each picks
   the regime-matched scenario for descriptive copy, then overrides
   the headline numbers with live values. Falls back to demo when
   live data is missing.
   ============================================================ */

function pickRegimeKey(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return 'EARLY_WARNING';
  if (s <= 24) return 'ALL_CLEAR';
  if (s <= 49) return 'EARLY_WARNING';
  return 'DANGER_ZONE';
}

function activityLevelFromScore(score) {
  const s = Number(score);
  if (s <= 24) return 'LOW';
  if (s <= 49) return 'MODERATE';
  if (s <= 74) return 'HIGH';
  return 'EXTREME';
}

function buildLiveVigilanteData(liveData, scenarios) {
  const vScore = Number(liveData?.signal?.subSignals?.vigilanteActivity);
  const srcMap = liveData?.signal?.subSignalSources || {};
  const base = scenarios[pickRegimeKey(vScore)] || scenarios.EARLY_WARNING;
  if (!Number.isFinite(vScore)) return base;

  const liveAuctions = Array.isArray(liveData?.auctions?.auctions) ? liveData.auctions.auctions : [];
  const auctions = liveAuctions.length
    ? liveAuctions.slice(0, 6).map((a) => ({
        date: a.date ? new Date(a.date) : new Date(),
        type: a.type || 'Treasury Auction',
        bidToCover: Number(a.bidToCover) || null,
        rating: a.rating || 'ADEQUATE',
        tailBps: (Number.isFinite(Number(a.highYield)) && Number.isFinite(Number(a.interestRate)))
          ? roundNumber((Number(a.highYield) - Number(a.interestRate)) * 100, 1)
          : null,
        note: 'Live TreasuryDirect auction result.',
      }))
    : base.auctions;

  const isLiveVig = srcMap.vigilanteActivity === 'live';
  return {
    ...base,
    activityScore: Math.round(vScore),
    activityLevel: activityLevelFromScore(vScore),
    auctions,
    dataSource: isLiveVig ? 'LIVE' : 'PLACEHOLDER',
    analysis: (isLiveVig
      ? `Live reading: bond-vigilante activity scores ${Math.round(vScore)}/100, computed from recent Treasury auction demand in the shared snapshot. `
      : `Bond-vigilante activity is still using a placeholder value until live auction data is connected. `) + base.analysis,
  };
}

function buildLiveFearData(liveData, scenarios) {
  const cScore = Number(liveData?.signal?.subSignals?.creditSpreads);
  const base = scenarios[pickRegimeKey(cScore)] || scenarios.EARLY_WARNING;
  const cs = liveData?.creditSpreads;
  const vol = liveData?.volatility;
  if (!cs && !vol && !Number.isFinite(cScore)) return base;

  const igCurrent = readFirstNumber(cs?.ig?.current, base.ig.current);
  const hyCurrent = readFirstNumber(cs?.hy?.current, base.hy.current);
  const moveCurrent = readFirstNumber(vol?.move?.current, base.move.current);
  const vixCurrent = readFirstNumber(vol?.vix?.current, base.vix.current);

  return {
    ...base,
    ig: { ...base.ig, current: roundNumber(igCurrent, 0) },
    hy: { ...base.hy, current: roundNumber(hyCurrent, 0) },
    move: {
      ...base.move,
      current: roundNumber(moveCurrent, 0),
      note: vol?.move?.source === 'PROXY_YIELD_VOL'
        ? 'Realized-volatility proxy from the 10Y yield.'
        : base.move.note,
    },
    vix: { ...base.vix, current: roundNumber(vixCurrent, 1) },
    dataSource: 'LIVE',
    analysis: `Live reading from the shared snapshot: investment-grade spreads ${roundNumber(igCurrent, 0)}bps, high-yield ${roundNumber(hyCurrent, 0)}bps, MOVE ${roundNumber(moveCurrent, 0)}, VIX ${roundNumber(vixCurrent, 1)}. ` + base.analysis,
  };
}

function buildLiveFlowData(liveData, scenarios) {
  const mScore = Number(liveData?.signal?.subSignals?.moneyFlows);
  const base = scenarios[pickRegimeKey(mScore)] || scenarios.EARLY_WARNING;
  const fed = liveData?.fed;
  const flows = liveData?.moneyFlows;
  if (!Number.isFinite(mScore) && !flows && !fed) {
    return { ...base, truthState: 'SCENARIO', truthNote: 'No live liquidity feed is connected, so this page is showing an illustrative scenario for education. The readings below are examples of what this regime looks like — not current market data.' };
  }

  const repoSpread = readFirstNumber(fed?.repoSpread, base.repo?.spread);
  const rrp = flows?.rrp;
  const liveRepo = base.repo
    ? {
        ...base.repo,
        spread: Number.isFinite(Number(repoSpread)) ? roundNumber(repoSpread, 1) : base.repo.spread,
      }
    : base.repo;

  return {
    ...base,
    repo: liveRepo,
    dataSource: 'LIVE',
    truthState: 'LIVE',
    truthNote: 'The Master Signal and repo reading are live. The detailed liquidity readings below illustrate what this regime typically looks like — treat them as educational context unless a dedicated live liquidity feed confirms them.',
    analysis: (rrp
      ? `Live reading: overnight reverse-repo balance is ${roundNumber(rrp.current, 0)} (cash parked at the Fed), and the repo spread is ${formatBps(repoSpread)}. `
      : `Live reading from the shared snapshot. Repo spread is ${formatBps(repoSpread)}. `) + base.analysis,
  };
}

function buildLiveStratData(liveData, scenarios) {
  const compositeScore = Number(liveData?.signal?.compositeScore);
  const gScore = Number(liveData?.signal?.subSignals?.globalBonds);
  const srcMap = liveData?.signal?.subSignalSources || {};
  const base = scenarios[pickRegimeKey(compositeScore)] || scenarios.EARLY_WARNING;
  if (!Number.isFinite(compositeScore)) return base;

  const isLiveGlobal = srcMap.globalBonds === 'live';
  const globalBonds = base.globalBonds
    ? {
        ...base.globalBonds,
        divergenceLevel: Number.isFinite(gScore) ? Math.round(gScore) : base.globalBonds.divergenceLevel,
        divergenceLabel: Number.isFinite(gScore) ? activityLevelFromScore(gScore) : base.globalBonds.divergenceLabel,
      }
    : base.globalBonds;

  return {
    ...base,
    globalBonds,
    dataSource: 'LIVE',
    analysis: (isLiveGlobal
      ? `Live reading: global-bond stress scores ${Math.round(gScore)}/100 from foreign 10-year yields in the shared snapshot. `
      : '') + base.analysis,
  };
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24 animate-fade-in" role="status" aria-live="polite">
      <div className="flex items-center gap-3 text-mentor-muted">
        <span className="w-4 h-4 rounded-full border-2 border-mentor-accent/40 border-t-mentor-accent animate-spin" />
        <span className="text-sm font-display">Loading…</span>
      </div>
    </div>
  );
}

function TrainingModeNotice({ activeTab, onOpenLearn, actionLabel = 'Learn Scenarios' }) {
  if (activeTab === 'learn') return null;

  const isDashboard = activeTab === 'dashboard';

  return (
    <div className={`glass-card p-4 animate-fade-in ${isDashboard ? 'border border-signal-yellow/25' : ''}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${isDashboard ? 'bg-signal-yellow' : 'bg-mentor-accent'} animate-pulse`} />
            <h4 className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-widest">
              {isDashboard ? 'Training View Active — Not Live Market Guidance' : 'Training Data Active'}
            </h4>
          </div>
          <p className="text-xs text-mentor-muted font-body leading-relaxed">
            {isDashboard
              ? 'Mission Control is showing a safe training fallback because the live proxy is not currently connected. Use Learn for scenario education, or start the Node server to return this page to live market data.'
              : 'This page is using scenario data until the live proxy connects. Educational scenario controls now live in the Learn tab.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenLearn}
          className="px-3 py-2 rounded-lg text-xs font-display font-semibold border border-mentor-accent/40 text-mentor-accent hover:bg-mentor-accent/10 transition-colors"
        >
          {actionLabel}
        </button>
      </div>
      <p className="text-[10px] text-mentor-muted text-center mt-3 font-body">
        Run <span className="font-mono text-mentor-accent">node server/index.js</span> in a separate terminal for live market data.
      </p>
    </div>
  );
}

const TAB_LABELS = {
  dashboard: 'Mission Control',
  yieldCurve: 'Yield Curve',
  vigilantes: 'Vigilantes',
  credit: 'Fear Gauge',
  flows: 'Money Flows',
  strategy: 'Strategist',
  simulator: 'Simulator',
  learn: 'Learn',
  alerts: 'Regime Alerts',
  replay: 'Replay',
  backtesting: 'Backtesting',
  probability: 'Probability',
  adminHealth: 'Admin Health',
  invites: 'Invites',
  waitlist: 'Waitlist',
  siteMode: 'Site Mode',
  feedback: 'Feedback',
  analytics: 'Admin Analytics',
  account: 'Account',
};

function getAnalyticsSessionId() {
  const key = 'bondMentorAnalyticsSession_v43g';
  try {
    let value = sessionStorage.getItem(key);
    if (!value) {
      value = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(key, value);
    }
    return value;
  } catch {
    return `${Date.now()}-session`;
  }
}

function BondMentorDashboard({ user, onLogout }) {
  const [activeScenario, setActiveScenario] = useState('ALL_CLEAR');
  const [activeTab, setActiveTab] = useState('dashboard');
  const presence = usePresenceHeartbeat(user);
  const [birthday, setBirthday] = useState(null);
  const [birthdayDismissed, setBirthdayDismissed] = useState(false);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [auctionWatch, setAuctionWatch] = useState(null);

  useEffect(() => {
    let active = true;
    fetchNavVisibility().then((result) => { if (active && result?.ok) setHiddenTabs(result.hidden || []); });
    fetchAuctionWatch().then((result) => { if (active && result?.ok) setAuctionWatch(result); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    // If the SuperAdmin hides the tab you're currently on, fall back to the dashboard.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- react to a visibility change
    if (activeTab !== 'dashboard' && hiddenTabs.includes(activeTab)) setActiveTab('dashboard');
  }, [hiddenTabs, activeTab]);

  useEffect(() => {
    let active = true;
    fetchBirthdayStatus().then((result) => {
      if (active && result?.ok && result.isBirthday) setBirthday(result);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const siteMode = window.__bondMentorSiteMode || null;
    trackAnalyticsEvent({
      page: activeTab,
      label: TAB_LABELS[activeTab] || activeTab,
      path: `/${activeTab}`,
      sessionId: getAnalyticsSessionId(),
      siteMode: siteMode?.effectiveMode || siteMode?.mode || 'live',
      referrer: document.referrer || '',
    });
  }, [activeTab]);

  // Live data hook — detects proxy server automatically
  const {
    mode, liveData, lastUpdated, error, isLoading, isLive,
    refreshData,
    resetAndRecheckLive,
  } = useLiveData(60000); // 1 minute refresh
  const lastSavedHistoryKeyRef = useRef(null);

  const handleStatusBarAction = () => {
    // Phase 41C:
    // User-facing pages route to Learn for scenario education.
    // Admin Health stays on Admin Health and jumps to the owner-only
    // Admin Testing Control Center instead of opening the public simulator.
    if (activeTab === 'adminHealth') {
      window.setTimeout(() => {
        document
          .getElementById('admin-testing-control-center')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return;
    }

    setActiveTab('learn');
  };

  const statusBarActionLabel = activeTab === 'adminHealth'
    ? 'Admin Preview Mode'
    : 'Learn Scenarios';

  const handleAdminResetRecheckLive = async () => {
    // Phase 41B:
    // The Admin Health "Reset / Recheck Live" button should act like a
    // return-to-truth control. It clears any educational scenario focus,
    // asks the proxy to clear/recheck live status, then reloads the live snapshot.
    setActiveScenario('ALL_CLEAR');
    return await resetAndRecheckLive();
  };


  // === DEMO MODE DATA ===
  const scenarioData = SCENARIOS[activeScenario];
  const demoSignal = useMemo(
    () => computeMasterSignal(scenarioData.subSignals),
    [scenarioData.subSignals]
  );
  const demoSummary = useMemo(
    () => generateSignalSummary(demoSignal),
    [demoSignal]
  );
  const demoCurveData = YIELD_CURVE_SCENARIOS[activeScenario];
  const demoFedData = FED_WATCH_SCENARIOS[activeScenario];
  const demoVigilanteData = VIGILANTE_SCENARIOS[activeScenario];
  const demoFearData = FEAR_GAUGE_SCENARIOS[activeScenario];
  const demoFlowData = MONEY_FLOW_SCENARIOS[activeScenario];
  const demoStratData = STRATEGIST_SCENARIOS[activeScenario];

  // === RESOLVE: Use live data if available, otherwise demo ===
  // In live mode, we use data from the proxy server.
  // In demo mode, we use the local scenario data files.
  // The components don't need to know which source — they get the same format.

  // Import ACTION_LEVELS so we can resolve string keys from the server
  // into the full objects the components expect.

  const masterSignal = useMemo(() => {
    if (isLive && liveData?.signal) {
      const live = liveData.signal;
      // The server returns actionLevel as a string key (e.g., "FULLY_INVESTED").
      // Components need the full object with label, exposure, color, description.
      const actionLevelObj = typeof live.actionLevel === 'string'
        ? ACTION_LEVELS[live.actionLevel] || ACTION_LEVELS.TIGHTEN_STOPS
        : live.actionLevel;

      return {
        signal: live.signal,
        compositeScore: live.compositeScore,
        actionLevel: actionLevelObj,
        subSignals: live.subSignals,
        exactCompositeScore: live.exactCompositeScore,
        rawWeightedCompositeScore: live.rawWeightedCompositeScore,
        breadthFloorApplied: live.breadthFloorApplied,
        roundingMethod: live.roundingMethod || 'Math.round',
        riskRegime: live.riskRegime,
        riskBreadth: live.riskBreadth,
        signalConfidence: liveData.signalConfidence,
        dataFreshness: liveData.dataFreshness || [],
        signalStability: live.signalStability || null,
        timestamp: new Date(live.computedAt),
      };
    }
    return {
      ...demoSignal,
      exactCompositeScore: demoSignal.exactCompositeScore || demoSignal.compositeScore,
      roundingMethod: 'Math.round',
      timestamp: demoSignal.timestamp || new Date(),
    };
  }, [isLive, liveData, demoSignal]);

  const summary = isLive && liveData?.signal
    ? generateSignalSummary(masterSignal)
    : demoSummary;
  const [liveSignalHistory, setLiveSignalHistory] = useState(() => {
    try {
      const saved = localStorage.getItem(SIGNAL_HISTORY_STORAGE_KEY);
      return saved ? cleanSignalHistory(JSON.parse(saved)) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!isLive || !liveData?.signal || liveData.signal.fallback) return;

    const live = liveData.signal;
    const stability = live.signalStability || {};
    const stabilityState = String(stability.state || 'CONFIRMED').toUpperCase();

    // Phase 33B: user-facing history records confirmed advice only.
    // Pending/developing watch states are shown in the Signal Stability panel,
    // but they should not clutter Recent Signal History as if they were advice changes.
    if (stability.enabled && !['CONFIRMED', 'CONFIRMED_TRANSITION'].includes(stabilityState)) return;
    const rawDate = live.computedAt || liveData.fetchedAt || new Date().toISOString();
    const minuteDate = new Date(rawDate);
    minuteDate.setSeconds(0, 0);
    const date = minuteDate.toISOString();
    const action = live.fallback
      ? 'API Setup Needed'
      : typeof live.actionLevel === 'string'
        ? (ACTION_LEVELS[live.actionLevel]?.label || live.actionLevel)
        : (live.actionLevel?.label || 'Live signal updated');

    const historyEntry = {
      date,
      signal: live.signal || 'YELLOW',
      score: Math.round(Number(live.compositeScore ?? 35)),
      action,
      patch: APP_PATCH_VERSION,
      savedAt: new Date().toISOString(),
      status: stabilityState === 'CONFIRMED_TRANSITION' ? 'Confirmed transition' : 'Confirmed signal',
      stabilityState,
      sourceMode: liveData.truthState === 'LIVE' ? 'Live feed' : 'Verified backup',
      truthState: liveData.truthState || 'UNKNOWN',
      confidenceLabel: liveData.signalConfidence?.label || liveData.signalConfidence?.level || null,
    };
    historyEntry.id = `${buildMinuteHistoryKey(historyEntry)}-${buildStateKey(historyEntry)}`;

    const historyKey = buildStableRunId(liveData, live);
    if (lastSavedHistoryKeyRef.current === historyKey) return;
    lastSavedHistoryKeyRef.current = historyKey;

    queueMicrotask(() => {
      setLiveSignalHistory((previous) => {
        const cleaned = cleanSignalHistory(previous);
        const latest = cleaned[0];

        // If this refresh has the same score/regime/action as the latest row,
        // keep the original timestamp. Recent Signal History should show when
        // advice changed, not every time polling reconfirmed the same state.
        if (latest && buildStateKey(latest) === buildStateKey(historyEntry)) {
          localStorage.setItem(SIGNAL_HISTORY_STORAGE_KEY, JSON.stringify(cleaned));
          return cleaned;
        }

        const withoutSameMinute = cleaned.filter((entry) => buildMinuteHistoryKey(entry) !== historyKey);
        const next = cleanSignalHistory([historyEntry, ...withoutSameMinute]);
        localStorage.setItem(SIGNAL_HISTORY_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    });
  }, [isLive, liveData]);

  const resolvedSignalHistory = useMemo(() => {
    if (!isLive) return SIGNAL_HISTORY;

    if (liveSignalHistory.length) return liveSignalHistory;

    if (liveData?.signal) {
      const live = liveData.signal;
      return [{
        date: live.computedAt || liveData.fetchedAt || new Date().toISOString(),
        signal: live.signal,
        score: live.compositeScore,
        action: live.fallback
          ? 'API Setup Needed'
          : typeof live.actionLevel === 'string'
            ? (ACTION_LEVELS[live.actionLevel]?.label || live.actionLevel)
            : (live.actionLevel?.label || 'Live signal updated'),
        status: live.signalStability?.state === 'CONFIRMED_TRANSITION' ? 'Confirmed transition' : 'Confirmed signal',
        stabilityState: live.signalStability?.state || 'CONFIRMED',
        sourceMode: liveData.truthState === 'LIVE' ? 'Live feed' : 'Verified backup',
        truthState: liveData.truthState || 'UNKNOWN',
        confidenceLabel: liveData.signalConfidence?.label || liveData.signalConfidence?.level || null,
      }];
    }

    return [];
  }, [isLive, liveSignalHistory, liveData]);


  const liveKeyMetrics = useMemo(() => buildLiveKeyMetrics(liveData), [liveData]);

  // Drop C — "Signal Honesty": classify how live each component is right now and
  // roll it up into a single "% live" figure for the master light.
  const gridSubSignals = useMemo(
    () => (isLive && liveData?.signal?.subSignals ? liveData.signal.subSignals : scenarioData.subSignals),
    [isLive, liveData, scenarioData]
  );
  const signalCoverage = useMemo(
    () => computeSignalCoverage(SUB_SIGNAL_META, liveData, isLive),
    [liveData, isLive]
  );

  // For tabs that need structured data, live mode currently passes through
  // demo data while individual endpoints are being built out.
  // As more live endpoints are completed, these will switch to live data.
  const curveData = useMemo(
    () => (isLive ? buildLiveYieldCurveData(liveData, demoCurveData) : demoCurveData),
    [isLive, liveData, demoCurveData]
  );
  const fedData = useMemo(
    () => (isLive ? buildLiveFedWatchData(liveData, demoFedData, curveData) : demoFedData),
    [isLive, liveData, demoFedData, curveData]
  );
  const vigilanteData = useMemo(
    () => (isLive ? buildLiveVigilanteData(liveData, VIGILANTE_SCENARIOS) : demoVigilanteData),
    [isLive, liveData, demoVigilanteData]
  );
  const fearData = useMemo(
    () => (isLive ? buildLiveFearData(liveData, FEAR_GAUGE_SCENARIOS) : demoFearData),
    [isLive, liveData, demoFearData]
  );
  const flowData = useMemo(
    () => (isLive ? buildLiveFlowData(liveData, MONEY_FLOW_SCENARIOS) : demoFlowData),
    [isLive, liveData, demoFlowData]
  );
  const stratData = useMemo(
    () => (isLive ? buildLiveStratData(liveData, STRATEGIST_SCENARIOS) : demoStratData),
    [isLive, liveData, demoStratData]
  );

  const macroEvents = useMemo(() => liveData?.macroEvents || {
    enabled: true,
    phase: '',
    riskLabel: 'Scenario Learning Context',
    label: 'Practice Macro Context',
    tone: 'blue',
    windowLabel: 'Educational only',
    summary: 'Macro event awareness appears here in live mode. In scenario learning mode, use the Learn tab to study how CPI, PCE, Fed meetings, jobs reports, and Treasury auctions can affect bond signals.',
    confirmationNote: 'Practice scenarios do not change live advice. They are examples for learning how major macro events can affect the signal engine.',
    events: [],
  }, [liveData?.macroEvents]);


  const riskProfiles = useMemo(() => {
    if (isLive && Array.isArray(liveData?.riskProfiles) && liveData.riskProfiles.length) {
      return liveData.riskProfiles;
    }
    return computeInstitutionalRiskProfiles({
      signal: masterSignal,
      macroEvents,
      confidence: liveData?.signalConfidence || masterSignal.signalConfidence || null,
    });
  }, [isLive, liveData?.riskProfiles, liveData?.signalConfidence, masterSignal, macroEvents]);

  return (
    <div className="min-h-screen flex flex-col bg-mentor-bg">
      <Header
        user={user}
        onOpenAccount={() => setActiveTab('account')}
        onLogout={onLogout}
        liveData={liveData}
        masterSignal={masterSignal}
        signalHistory={resolvedSignalHistory}
        siteMode={window.__bondMentorSiteMode || null}
        auctionWatch={auctionWatch}
      />
      <Navigation activeTab={activeTab} onTabChange={setActiveTab} user={user} hiddenTabs={hiddenTabs} auctionAlert={auctionWatch?.alert} />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8">
        <BannerBar />
        {/* Data Mode Indicator — hide on Simulator tab (it has its own) */}
        {activeTab !== 'simulator' && (
          <div className="mb-8">
            {isLive ? (
              <LiveModeIndicator
                mode={mode}
                lastUpdated={lastUpdated}
                isLoading={isLoading}
                error={error}
                onRefresh={refreshData}
                onOpenLearn={handleStatusBarAction}
                actionLabel={statusBarActionLabel}
              />
            ) : mode === 'CHECKING' ? (
              <LiveModeIndicator mode="CHECKING" />
            ) : (
              <TrainingModeNotice
                activeTab={activeTab}
                onOpenLearn={handleStatusBarAction}
                actionLabel={statusBarActionLabel}
              />
            )}
          </div>
        )}

        {/* === DASHBOARD TAB === */}
        {activeTab === 'dashboard' && (
          <>
            {/* Phase 37C — Mission Control Hero Layout Polish
                The main regime gauge now sits beside the main signal summary,
                so beginners see the score and explanation as one decision area. */}
            <section className="mb-8 animate-fade-in" aria-label="Mission Control current signal">
              <div className="grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)] gap-6 items-stretch">
                <div className="glass-card border border-mentor-border/70 p-6 flex flex-col items-center justify-center min-h-[300px]">
                  <MasterSignalLight
                    signal={masterSignal.signal}
                    score={masterSignal.compositeScore}
                    coverage={signalCoverage}
                  />
                  <div className="mt-5 text-center border-t border-mentor-border/60 pt-4 w-full">
                    <p className="text-[0.68rem] uppercase tracking-[0.22em] text-mentor-subtle font-display font-bold mb-1">
                      Current Reading
                    </p>
                    <p className="text-xs text-mentor-muted font-mono">
                      {isLive ? 'Live' : 'Demo'} — {formatDateTime(masterSignal.timestamp || new Date())}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-5 min-w-0">
                  <SignalSummary summary={summary} />
                  <RiskRegimeBoundaries
                    signal={masterSignal.signal}
                    score={masterSignal.compositeScore}
                    exactScore={masterSignal.exactCompositeScore}
                    riskRegime={masterSignal.riskRegime}
                    rawWeightedScore={masterSignal.rawWeightedCompositeScore}
                    breadthFloorApplied={masterSignal.breadthFloorApplied}
                    riskBreadth={masterSignal.riskBreadth}
                  />
                  <SignalStabilityNotice stability={masterSignal.signalStability} />
                </div>
              </div>
            </section>

            <div className="mb-8">
              <CollapsibleSection
                id="macro-calendar-watch"
                eyebrow="Calendar Watch"
                title="Macro Calendar Watch"
                summary="Shows whether CPI, PCE, Fed meetings, jobs reports, Treasury auctions, or other macro events are adding short-term pressure."
                badge={macroEvents?.riskLabel || macroEvents?.label || 'Macro context'}
                defaultOpen={Boolean(macroEvents?.events?.length) || ['yellow', 'red', 'orange'].includes(String(macroEvents?.tone || '').toLowerCase())}
              >
                <MacroEventAwareness macro={macroEvents} />
              </CollapsibleSection>
            </div>

            <div className="mb-8">
              <CollapsibleSection
                id="treasury-auction-watch"
                eyebrow="Auction Watch"
                title="Treasury Auction Watch"
                summary="Grades recent Treasury auctions in plain English — how eagerly the world is lending to the U.S., and what to watch for."
                badge="Context only"
                defaultOpen={false}
              >
                <AuctionWatchCard compact managed data={auctionWatch} />
              </CollapsibleSection>
            </div>

            <div className="mb-8">
              <CollapsibleSection
                id="commodities-watch"
                eyebrow="Inflation Tell"
                title="Commodities Watch"
                summary="Reads oil, gold, and metals in plain English — when they climb together, inflation pressure is building, which can push bond yields up."
                badge="Context only"
                defaultOpen={false}
              >
                <CommoditiesCard compact />
              </CollapsibleSection>
            </div>

            <div className="mb-8">
              <ActionPanel actionLevel={masterSignal.actionLevel} />
            </div>

            <div className="mb-8">
              <SubSignalGrid
                subSignals={gridSubSignals}
                currentDrop={CURRENT_DROP}
                coverage={signalCoverage}
              />
            </div>
            <div className="mb-8">
              <KeyMetrics metrics={isLive ? liveKeyMetrics : scenarioData.keyMetrics} isLive={isLive} />
            </div>
            {isLive && (liveData?.signalConfidence || liveData?.dataFreshness?.length) && (
              <div className="mb-8">
                <DataFreshnessConfidence
                  confidence={liveData.signalConfidence}
                  freshness={liveData.dataFreshness || []}
                />
              </div>
            )}
            <div className="mb-8">
              <CollapsibleSection
                id="institutional-risk-profiles"
                eyebrow="Risk Profiles"
                title="Institutional Risk Profiles"
                summary="Shows how conservative, balanced, growth, income/yield, and institutional-style readers may interpret the same signal."
                badge={`${riskProfiles?.length || 0} profiles`}
                defaultOpen={true}
              >
                <InstitutionalRiskProfiles profiles={riskProfiles} />
              </CollapsibleSection>
            </div>
            {isLive && liveData?.probabilityForecast && (
              <div className="mb-8">
                <CollapsibleSection
                  id="predictive-regime-probability"
                  eyebrow="Probability Lens"
                  title="Predictive Regime Probability Engine"
                  summary="Estimates where the next clean daily snapshot may lean without overriding the confirmed Mission Control signal."
                  badge={liveData.probabilityForecast?.mostLikely?.label || 'Probability lens'}
                  defaultOpen={true}
                >
                  <PredictiveRegimeProbability forecast={liveData.probabilityForecast} compact />
                </CollapsibleSection>
              </div>
            )}
            {isLive && (
              <div className="mb-8">
                <CollapsibleSection
                  id="what-changed"
                  eyebrow="Snapshot Compare"
                  title="What Changed?"
                  summary="Compares the newest clean snapshot with the prior clean snapshot so you can see what moved and why."
                  badge="Snapshot comparison"
                  defaultOpen={true}
                >
                  <WhatChangedSummary />
                </CollapsibleSection>
              </div>
            )}
            <div className="mb-8">
              <CollapsibleSection
                id="recent-signal-history"
                eyebrow="Signal history"
                title="Recent Signal History"
                summary="Shows the latest 5 confirmed records by default with filters for regime, status, source, and date range."
                badge={`${resolvedSignalHistory?.length || 0} records`}
                defaultOpen={true}
              >
                <SignalHistory history={resolvedSignalHistory} />
              </CollapsibleSection>
            </div>
          </>
        )}

        {/* === OTHER TABS (lazy-loaded) === */}
        <Suspense fallback={<PageLoader />}>
        {activeTab === 'yieldCurve' && <YieldCurvePage curveData={curveData} fedData={fedData} />}
        {activeTab === 'vigilantes' && <VigilantePage vigilanteData={vigilanteData} />}
        {activeTab === 'credit' && <FearGaugePage fearData={fearData} />}
        {activeTab === 'flows' && <MoneyFlowsPage flowData={flowData} />}
        {activeTab === 'strategy' && <StrategistPage stratData={stratData} />}
        {activeTab === 'simulator' && <SimulatorPage />}
        {activeTab === 'learn' && (
          <LearnPage
            activeScenario={activeScenario}
            onScenarioChange={setActiveScenario}
            isLive={isLive}
          />
        )}
        {activeTab === 'replay' && <HistoricalReplayPage />}
        {activeTab === 'alerts' && <RegimeAlertsPage />}
        {activeTab === 'backtesting' && <BacktestingPage />}
        {activeTab === 'probability' && <ProbabilityPage />}
        {activeTab === 'account' && <AccountPage user={user} onLogout={onLogout} />}

        {activeTab === 'adminHealth' && (
          <AdminHealthPage
            onOpenSimulator={() => setActiveTab('simulator')}
            onOpenLearn={() => setActiveTab('learn')}
            onResetRecheckLive={handleAdminResetRecheckLive}
          />
        )}

        {activeTab === 'invites' && user?.permissions?.canManageInvites && (
          <InviteAdminPage />
        )}

        {activeTab === 'waitlist' && user?.permissions?.canAccessAdmin && (
          <WaitlistAdminPage />
        )}

        {activeTab === 'siteMode' && user?.permissions?.canAccessAdmin && (
          <SiteModeAdminPage initialSiteMode={window.__bondMentorSiteMode || null} onSaved={(nextMode) => { window.__bondMentorSiteMode = nextMode; }} />
        )}

        {activeTab === 'feedback' && user?.permissions?.canAccessAdmin && (
          <FeedbackAdminPage />
        )}

        {activeTab === 'messages' && user?.permissions?.canAccessAdmin && (
          <MessagesAdminPage user={user} />
        )}

        {activeTab === 'directory' && user?.permissions?.canAccessAdmin && (
          <UserDirectoryPage />
        )}

        {activeTab === 'billing' && user?.permissions?.canAccessAdmin && (
          <BillingAdminPage user={user} />
        )}

        {activeTab === 'charity' && user?.permissions?.canAccessAdmin && (
          <CharityAdminPage user={user} />
        )}

        {activeTab === 'banners' && user?.permissions?.canAccessAdmin && (
          <BannerAdminPage user={user} />
        )}

        {activeTab === 'menuAccess' && user?.permissions?.canAccessAdmin && (
          <NavVisibilityAdminPage user={user} />
        )}

        {activeTab === 'macroCalendar' && user?.permissions?.canAccessAdmin && (
          <MacroCalendarAdminPage user={user} />
        )}

        {activeTab === 'secResearch' && user?.permissions?.canAccessAdmin && (
          <SecResearchPage />
        )}

        {activeTab === 'analytics' && user?.permissions?.canAccessAdmin && (
          <AdminAnalyticsPage user={user} />
        )}

        {activeTab === 'backups' && user?.permissions?.canAccessAdmin && (
          <BackupMigrationPage user={user} />
        )}
        </Suspense>
      </main>

      <Footer />
      <FeedbackWidget user={user} siteMode={window.__bondMentorSiteMode || null} />
      {presence.enabled && <TesterChatWidget user={user} unread={presence.unread} />}
      {birthday?.isBirthday && !birthdayDismissed && (
        <BirthdayCelebrationPage status={birthday} onClose={() => setBirthdayDismissed(true)} />
      )}
    </div>
  );
}


function readPublicView() {
  const path = window.location.pathname;
  if (path.startsWith('/waitlist') || path.startsWith('/join-waitlist')) return 'waitlist';
  if (path.startsWith('/signin')) return 'signin';
  return null;
}

export default function App() {
  const [authStatus, setAuthStatus] = useState(null);
  const [siteMode, setSiteMode] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  // Coherent public router: which not-logged-in view to show. null = the site
  // mode's default page (login/launch/sales/waitlist). Navigation is gentle
  // (no full reload) and syncs with the browser back/forward buttons.
  const [publicView, setPublicView] = useState(() => readPublicView());

  useEffect(() => {
    const onPop = () => setPublicView(readPublicView());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function goSignIn() { window.history.pushState({}, '', '/signin'); setPublicView('signin'); }
  function goWaitlist() { window.history.pushState({}, '', '/waitlist'); setPublicView('waitlist'); }
  function goHome() { window.history.pushState({}, '', '/'); setPublicView(null); }

  async function refreshAuth() {
    setAuthLoading(true);
    const [status, modeStatus] = await Promise.all([fetchAuthStatus(), fetchSiteMode()]);
    const resolvedMode = modeStatus || status?.siteMode || { ok: true, effectiveMode: 'live', mode: 'live', source: 'fallback' };
    window.__bondMentorSiteMode = resolvedMode;
    setSiteMode(resolvedMode);
    setAuthStatus(status || { enabled: true, authenticated: false, needsBootstrap: true, siteMode: resolvedMode });
    setUser(status?.authenticated ? status.user : null);
    setAuthLoading(false);
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate data fetch / one-time init on mount
    refreshAuth();
  }, []);

  async function handleLogin(email, password) {
    const result = await loginRequest(email, password);
    if (result?.ok && result.user) {
      const [status, modeStatus] = await Promise.all([fetchAuthStatus(), fetchSiteMode()]);
      const resolvedMode = modeStatus || status?.siteMode || siteMode;
      window.__bondMentorSiteMode = resolvedMode;
      setSiteMode(resolvedMode);
      setAuthStatus(status);
      setUser(result.user);
      return { ok: true };
    }
    return result || { ok: false, message: 'Sign in failed. Please try again.' };
  }

  async function handleLogout() {
    await logoutRequest();
    setUser(null);
    const [status, modeStatus] = await Promise.all([fetchAuthStatus(), fetchSiteMode()]);
    const resolvedMode = modeStatus || status?.siteMode || { ok: true, effectiveMode: 'live', mode: 'live', source: 'fallback' };
    window.__bondMentorSiteMode = resolvedMode;
    setSiteMode(resolvedMode);
    setAuthStatus(status || { enabled: true, authenticated: false, siteMode: resolvedMode });
  }

  const isInviteRoute = window.location.pathname.startsWith('/invite/') || new URLSearchParams(window.location.search).has('token');
  const effectiveSiteMode = siteMode?.effectiveMode || siteMode?.mode || 'live';
  const isAdminUser = Boolean(user?.permissions?.canAccessAdmin);
  // The public waitlist is open in the soft-launch modes; in Live/Maintenance it
  // is intentionally closed (so we never advertise a door that's locked).
  const waitlistOpen = ['waitlist', 'launch', 'sales'].includes(effectiveSiteMode);

  // Wait for the secure-access check to resolve before routing, so we never
  // briefly render the invite/launch/sales pages — or re-look-up an already-used
  // invite token — during the loading window.
  if (authLoading) {
    return (
      <div className="min-h-screen bg-mentor-bg flex items-center justify-center px-4">
        <div className="glass-card p-6 border border-mentor-border/60 text-center">
          <div className="w-10 h-10 rounded-full border-2 border-mentor-accent/30 border-t-mentor-accent animate-spin mx-auto mb-4" />
          <p className="font-display font-bold text-mentor-text">Checking secure access...</p>
          <p className="text-xs text-mentor-muted mt-1">Secure access and site mode are loading.</p>
        </div>
      </div>
    );
  }

  // Maintenance: non-admins (logged in or out) see the maintenance page. A
  // logged-out admin can choose Admin Sign In to reach the normal full login.
  if (effectiveSiteMode === 'maintenance' && !isAdminUser) {
    if (publicView === 'signin' && !user) {
      return <LoginPage authStatus={authStatus} onLogin={handleLogin} waitlistOpen={false} onBack={goHome} backLabel="Back to maintenance" />;
    }
    return <MaintenancePage siteMode={siteMode} onAdminSignIn={goSignIn} />;
  }

  // Logged-in members go to the dashboard (admins bypass maintenance to here).
  if (user) {
    return <BondMentorDashboard user={user} onLogout={handleLogout} />;
  }

  // Invite registration (logged-out visitor with an invite link).
  if (isInviteRoute) {
    return <InviteRegisterPage onRegistered={(newUser) => { window.history.replaceState({}, '', '/'); setPublicView(null); setUser(newUser); }} onBackToLogin={goSignIn} />;
  }

  // Explicit "I want the waitlist" — only honored where the waitlist is open.
  if (publicView === 'waitlist' && waitlistOpen) {
    return <WaitlistPage onBackToLogin={goSignIn} />;
  }

  // Explicit "I want to sign in."
  if (publicView === 'signin') {
    return <LoginPage authStatus={authStatus} onLogin={handleLogin} waitlistOpen={waitlistOpen} onOpenWaitlist={goWaitlist} onBack={goHome} />;
  }

  // Otherwise, the site mode's default public page.
  if (effectiveSiteMode === 'launch') {
    return <LaunchCelebrationPage siteMode={siteMode} onSignIn={goSignIn} />;
  }
  if (effectiveSiteMode === 'sales') {
    return <SalesPage siteMode={siteMode} onRequestAccess={goWaitlist} onSignIn={goSignIn} />;
  }
  if (effectiveSiteMode === 'waitlist') {
    return <WaitlistPage onBackToLogin={goSignIn} />;
  }

  // Live (default): the secure sign-in screen. Waitlist is closed in Live.
  return <LoginPage authStatus={authStatus} onLogin={handleLogin} waitlistOpen={false} onOpenWaitlist={goWaitlist} />;
}
